import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import Icon from '../../../../components/atoms/Icon'
import { ICON_NAMES } from '../../../../constants'
import SydButton from '../../../../components/commonDesign/Button'

const useStyles = makeStyles(() => ({
  container: {
    margin: '16px 0',
    padding: '20px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-md)',
    backgroundColor: 'var(--dai-color-surface4)',
    fontFamily: 'var(--font-sans)',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    boxSizing: 'border-box'
  },
  title: {
    fontSize: 'var(--font-size-lg)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--dai-color-text)',
    marginBottom: '16px',
    fontFamily: 'var(--font-sans)',
    lineHeight: 1.4
  },
  progressBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '20px'
  },
  progressDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-neutral-300)',
    transition: 'all 0.2s ease'
  },
  progressDotActive: {
    backgroundColor: 'var(--color-primary-500)',
    transform: 'scale(1.25)'
  },
  progressDotCompleted: {
    backgroundColor: 'var(--color-primary-400)'
  },
  progressText: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-neutral-500)',
    marginLeft: 'auto'
  },
  questionText: {
    fontSize: 'var(--font-size-md)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--dai-color-text)',
    marginBottom: '4px',
    lineHeight: 1.5
  },
  selectionHint: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-neutral-500)',
    marginBottom: '12px'
  },
  optionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '16px'
  },
  option: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '10px 14px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-md)',
    backgroundColor: 'var(--color-neutral-050)',
    transition: 'all 0.15s ease'
  },
  optionSelected: {
    borderColor: 'var(--color-primary-500)',
    backgroundColor: 'var(--color-primary-050)'
  },
  indicator: {
    width: '18px',
    height: '18px',
    marginTop: '1px',
    borderRadius: '50%',
    border: '2px solid var(--color-neutral-400)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.15s ease'
  },
  indicatorCheckbox: {
    borderRadius: '3px'
  },
  indicatorSelected: {
    backgroundColor: 'var(--color-primary-500)',
    borderColor: 'var(--color-primary-500)'
  },
  indicatorDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: 'white'
  },
  checkmark: {
    color: 'white',
    fontSize: '11px',
    lineHeight: 1
  },
  optionLabel: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--dai-color-text)',
    lineHeight: 1.4
  },
  otherAnswer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '10px 14px',
    border: '1px solid var(--color-primary-500)',
    borderRadius: 'var(--border-radius-md)',
    backgroundColor: 'var(--color-primary-050)'
  },
  otherLabel: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--dai-color-text)',
    fontStyle: 'italic',
    lineHeight: 1.4
  },
  skippedText: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-neutral-500)',
    fontStyle: 'italic',
    padding: '10px 14px'
  },
  navigation: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: '1px solid var(--color-neutral-200)'
  },
  navLeft: {
    display: 'flex',
    gap: '8px'
  },
  navRight: {
    display: 'flex',
    gap: '8px'
  },
  completedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-success)',
    fontWeight: 'var(--font-weight-semibold)'
  }
}))

function QuestionnaireAnswersSummary ({ data }) {
  const classes = useStyles()
  const [currentIndex, setCurrentIndex] = useState(0)

  const items = data.questions

  const handleNext = useCallback(() => {
    if (!items) return
    if (currentIndex < items.length - 1) {
      setCurrentIndex(prev => prev + 1)
    }
  }, [currentIndex, items])

  const handleBack = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1)
    }
  }, [currentIndex])

  if (!items || !Array.isArray(items) || items.length === 0) return null

  const currentItem = items[currentIndex]
  const isMultiAnswer = currentItem.answer && currentItem.answer.length > 1
  const isLastQuestion = currentIndex === items.length - 1

  return (
    <div className={classes.container}>
      <div className={classes.title}>{data.label || 'Questionnaire Answers'}</div>

      {items.length > 1 && (
        <div className={classes.progressBar}>
          {items.map((_, idx) => (
            <div
              key={idx}
              className={clsx(
                classes.progressDot,
                idx === currentIndex && classes.progressDotActive,
                idx < currentIndex && classes.progressDotCompleted
              )}
            />
          ))}
          <span className={classes.progressText}>
            {currentIndex + 1} of {items.length}
          </span>
        </div>
      )}

      <div className={classes.questionText}>{currentItem.question}</div>

      {isMultiAnswer && (
        <div className={classes.selectionHint}>Multiple selections</div>
      )}

      <div className={classes.optionsContainer}>
        {currentItem.skipped ? (
          <div className={classes.skippedText}>Skipped</div>
        ) : (
          <>
            {currentItem.answer.map((a, aIdx) => (
              <div key={aIdx} className={clsx(classes.option, classes.optionSelected)}>
                <div className={clsx(
                  classes.indicator,
                  isMultiAnswer && classes.indicatorCheckbox,
                  classes.indicatorSelected
                )}
                >
                  {isMultiAnswer
                    ? <span className={classes.checkmark}>✓</span>
                    : <div className={classes.indicatorDot} />}
                </div>
                <span className={classes.optionLabel}>{a}</span>
              </div>
            ))}
            {currentItem.other && (
              <div className={classes.otherAnswer}>
                <div className={clsx(
                  classes.indicator,
                  isMultiAnswer && classes.indicatorCheckbox,
                  classes.indicatorSelected
                )}
                >
                  {isMultiAnswer
                    ? <span className={classes.checkmark}>✓</span>
                    : <div className={classes.indicatorDot} />}
                </div>
                <span className={classes.otherLabel}>Other: {currentItem.other}</span>
              </div>
            )}
          </>
        )}
      </div>

      <div className={classes.navigation}>
        <div className={classes.navLeft}>
          {currentIndex > 0 && (
            <SydButton variant='ghost' onClick={handleBack} size='sm'>
              Back
            </SydButton>
          )}
        </div>
        <div className={classes.navRight}>
          <span className={classes.completedBadge}>
            <Icon name={ICON_NAMES.checkCircle} customSize='14px' />
            Completed
          </span>
          {!isLastQuestion && (
            <SydButton variant='primary' onClick={handleNext} size='sm'>
              Next
            </SydButton>
          )}
        </div>
      </div>
    </div>
  )
}

QuestionnaireAnswersSummary.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string,
    questions: PropTypes.arrayOf(PropTypes.shape({
      question: PropTypes.string.isRequired,
      answer: PropTypes.arrayOf(PropTypes.string),
      other: PropTypes.string,
      skipped: PropTypes.bool
    })).isRequired
  }).isRequired
}

export default QuestionnaireAnswersSummary
